import { z } from "zod";

/** Optional so existing invoices and saved defaults remain unchanged. */
export const invoicePaymentSchema = z.object({
  enabled: z.boolean(),
  recipient: z.string().max(70),
  iban: z.string().max(60),
  bic: z.string().max(20),
});
export type InvoicePayment = z.infer<typeof invoicePaymentSchema>;

export function normalizeIban(iban: string): string {
  return iban.replace(/\s/g, "").toUpperCase();
}

export function isValidIban(value: string): boolean {
  const iban = normalizeIban(value);
  // SEPA IBAN lengths; reject valid-checksum but incorrectly sized accounts.
  const lengths: Record<string, number> = {
    AD: 24, AT: 20, BE: 16, BG: 22, CH: 21, CY: 28, CZ: 24, DE: 22,
    DK: 18, EE: 20, ES: 24, FI: 18, FR: 27, GB: 22, GI: 23, GR: 27,
    HR: 21, HU: 28, IE: 22, IS: 26, IT: 27, LI: 21, LT: 20, LU: 20,
    LV: 21, MC: 27, MT: 31, NL: 18, NO: 15, PL: 28, PT: 25, RO: 24,
    SE: 24, SI: 19, SK: 24, SM: 27, VA: 22, AL: 28, ME: 22, MK: 19, RS: 22,
  };
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban) || iban.length !== lengths[iban.slice(0, 2)]) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const char of rearranged) {
    const digits = char >= "A" ? String(char.charCodeAt(0) - 55) : char;
    for (const digit of digits) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

/** EPC069-12 v002, UTF-8, SCT; unstructured remittance, no structured reference. */
export function buildEpcPayment(payment: InvoicePayment, total: number, invoiceNumber: string): string {
  const recipient = payment.recipient.trim();
  const bic = payment.bic.replace(/\s/g, "").toUpperCase();
  const reference = invoiceNumber.trim();
  if (!recipient || /[\r\n]/.test(recipient)) throw new Error("Bitte einen gültigen Kontoinhaber für den Zahlungs-QR-Code eintragen.");
  if (!isValidIban(payment.iban)) throw new Error("Bitte eine gültige IBAN für den Zahlungs-QR-Code eintragen.");
  if (bic && !/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(bic)) throw new Error("Bitte einen gültigen BIC eintragen oder das BIC-Feld leer lassen.");
  if (!Number.isFinite(total) || total < 0.01 || total > 999999999.99) throw new Error("Der Zahlungs-QR-Code benötigt einen positiven Rechnungsbetrag bis 999.999.999,99 Euro.");
  if (!reference || /[\r\n]/.test(reference) || `Rechnung ${reference}`.length > 140) throw new Error("Bitte eine gültige Rechnungsnummer für den Zahlungs-QR-Code eintragen.");
  const payload = ["BCD", "002", "1", "SCT", bic, recipient, normalizeIban(payment.iban),
    `EUR${total.toFixed(2)}`, "", "", `Rechnung ${reference}`].join("\n");
  if (new TextEncoder().encode(payload).length > 331) throw new Error("Die Zahlungsdaten sind für den SEPA-QR-Code zu lang.");
  return payload;
}
